<script setup>
import {
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  watch,
  computed,
  provide,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import pipelineCardCable from 'dashboard/helper/pipelineCardCable';
import LabelsAPI from 'dashboard/api/labels';
import PipelineTab from './components/PipelineTab.vue';
import ListTab from './components/ListTab.vue';
import AgendaTab from './components/AgendaTab.vue';
import PipelinesManager from './components/PipelinesManager.vue';
import MessageTemplates from './components/MessageTemplates.vue';
import PipelineCardDetailView from './components/PipelineCardDetailView.vue';
import Spinner from 'shared/components/Spinner.vue';
import { usePipelinePermissions } from './composables/usePipelinePermissions';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const currentView = ref('pipeline');
const selectedItemId = ref(null);
const forceReloadCounter = ref(0);
const isTransitioning = ref(false); // Phase 1 - UX Improvements
const isBootstrapping = ref(true);
const bootstrapError = ref('');
let viewTransitionRequestId = 0;
let accountGeneration = 0;
let bootstrapStartedGeneration = -1;
let suppressRouteWatcher = false;
let isViewMounted = false;
let routeRequestId = 0;

// Helper functions for URL slugs
const generateSlug = pipeline => {
  if (!pipeline) return '';
  const name = pipeline.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${pipeline.id}-${name}`;
};

const extractIdFromSlug = slug => {
  if (!slug) return null;
  const match = slug.match(/^(\d+)/);
  // Return as string to match store IDs (pipelines store converts IDs to strings)
  return match ? match[1] : null;
};

const parseRouteItemId = itemId => {
  if (typeof itemId !== 'string' || !/^[1-9]\d*$/.test(itemId)) return null;

  const parsedItemId = Number(itemId);
  return Number.isSafeInteger(parsedItemId) ? parsedItemId : null;
};

// Map view names to URL paths and vice versa
// Note: 'dashboard' is handled separately - redirects to UnifiedPipelineDashboard
const viewToPath = {
  pipeline: 'board',
  list: 'list',
  agenda: 'agenda',
  pipelines: 'pipelines',
  templates: 'templates',
  'item-detail': 'item',
};

const pathToView = Object.fromEntries(
  Object.entries(viewToPath).map(([k, v]) => [v, k])
);

// Navigate to URL without triggering reload
const navigateToUrl = (
  pipelineSlug,
  view,
  itemId = null,
  replaceCurrent = false
) => {
  const accountId = route.params.accountId;
  let routeName = 'pipeline_board';
  const params = { accountId };

  if (pipelineSlug) {
    params.pipelineSlug = pipelineSlug;
  }

  if (view) {
    params.view = viewToPath[view] || view;
  }

  if (itemId) {
    params.itemId = itemId;
    routeName = 'pipeline_item_detail';
  } else if (pipelineSlug && view) {
    routeName = 'pipeline_board';
  } else if (pipelineSlug) {
    routeName = 'pipeline_with_funnel';
  } else {
    routeName = 'pipeline_index';
  }

  const navigate = replaceCurrent ? router.replace : router.push;
  return navigate({ name: routeName, params });
};

const isLoading = computed(() => store.state.pipelineCards.loading);
const pipelineCards = computed(() => {
  // P0.4 — sem logger.debug aqui. Era .map(i => i.id) sobre 2000+ items
  // a cada re-eval do computed (alocava array novo de IDs descartado).
  return store.getters['pipelineCards/getPipelineCardItems'] || [];
});
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

// Permission guard — redirect to first accessible pipeline when user lacks access
const { canViewPipeline, isAdmin } = usePipelinePermissions(selectedPipeline);

const isPipelineAccessible = pipeline => {
  if (!pipeline) return false;
  if (isAdmin.value) return true;

  const agents = Array.isArray(pipeline.settings?.agents)
    ? pipeline.settings.agents
    : [];
  if (agents.length === 0) return true;

  const currentUser = store.getters.getCurrentUser;
  return agents.some(agent => {
    const agentId =
      agent !== null && typeof agent === 'object' ? agent.id : agent;
    return String(agentId) === String(currentUser?.id);
  });
};

const firstAccessiblePipeline = pipelines =>
  pipelines.find(isPipelineAccessible) || null;

const replaceInvalidPipelineRoute = async (
  pipelines,
  requestedView,
  requestIsCurrent = () => true
) => {
  if (!requestIsCurrent()) return null;
  const fallbackPipeline = firstAccessiblePipeline(pipelines);
  if (!fallbackPipeline) {
    bootstrapError.value = t('PIPELINE.PIPELINE_NOT_FOUND');
    return null;
  }

  const fallbackView =
    requestedView && requestedView !== 'item-detail'
      ? requestedView
      : 'pipeline';
  selectedItemId.value = null;
  await store.dispatch('pipelines/setSelectedPipeline', fallbackPipeline);
  if (!requestIsCurrent()) return null;
  // The target dataset must win before the component changes mode; otherwise
  // list/agenda state can be rendered as a board after an invalid URL.
  // eslint-disable-next-line no-use-before-define
  await loadCanonicalView(fallbackView, {}, requestIsCurrent);
  if (!requestIsCurrent()) return null;
  await navigateToUrl(generateSlug(fallbackPipeline), fallbackView, null, true);
  return { pipeline: fallbackPipeline, view: fallbackView };
};

watch(
  () => [selectedPipeline.value, canViewPipeline.value],
  ([pipeline, canView]) => {
    if (!pipeline || isAdmin.value) return;
    if (!canView) {
      // Try to find first accessible pipeline in the list
      const allPipelines = store.getters['pipelines/getPipelines'];
      const accessible = firstAccessiblePipeline(allPipelines);
      if (accessible) {
        store.dispatch('pipelines/setSelectedPipeline', accessible);
      } else {
        // No accessible pipeline: show pipelines manager view
        currentView.value = 'pipelines';
      }
    }
  }
);

const labelsMap = ref({});

// Controle centralizado de carregamento
const isInitialLoad = ref(true);

// UX Card-Q1.2 (2026-04-22): centralizar leitura de localStorage.
// Antes cada PipelineCardItem lia 2× no onMounted (visibility +
// collapsed). Com 100 cards = 200 reads sincronas no boot do board.
// Agora PipelineView lê 1× e provê refs reativos via provide/inject.
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(
  () => store.getters.getCurrentAccountId ?? null
);
const normalizeAccountId = value => {
  if (value === undefined || value === null) return null;
  return String(value).trim() || null;
};
const routeAccountId = computed(() =>
  normalizeAccountId(route.params.accountId)
);
const normalizedCurrentAccountId = computed(() =>
  normalizeAccountId(currentAccountId.value)
);
const targetAccountId = computed(
  () => routeAccountId.value || normalizedCurrentAccountId.value
);
const activeAccountId = ref(null);
const accountContextIsReady = accountId =>
  accountId !== null &&
  (routeAccountId.value === null ||
    routeAccountId.value === normalizedCurrentAccountId.value);
const captureAccountContext = accountId => ({
  accountId: normalizeAccountId(accountId),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  isViewMounted &&
  context.accountId !== null &&
  context.generation === accountGeneration &&
  context.accountId === activeAccountId.value &&
  context.accountId === targetAccountId.value &&
  accountContextIsReady(context.accountId);
const collapsedStorageKey = computed(
  () =>
    `u${currentUserId.value}_a${currentAccountId.value}_pipeline_collapsed_items`
);

const safeReadJSON = key => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const cardVisibility = ref({});
const collapsedItems = ref({});

const loadCardVisibility = () => {
  cardVisibility.value = safeReadJSON('pipeline_cards_visibility');
};
const loadCollapsedItems = () => {
  collapsedItems.value = safeReadJSON(collapsedStorageKey.value);
};

const updateCardVisibility = (cardId, settings) => {
  cardVisibility.value = {
    ...cardVisibility.value,
    [cardId]: { ...cardVisibility.value[cardId], ...settings },
  };
  localStorage.setItem(
    'pipeline_cards_visibility',
    JSON.stringify(cardVisibility.value)
  );
};

const updateCollapsedItem = (cardId, isCollapsed) => {
  collapsedItems.value = {
    ...collapsedItems.value,
    [cardId]: isCollapsed,
  };
  localStorage.setItem(
    collapsedStorageKey.value,
    JSON.stringify(collapsedItems.value)
  );
};

// Bulk (Recolher/Expandir todos da coluna): um único write reativo +
// localStorage para N cards — o caminho antigo escrevia direto no storage,
// invisível ao ref injetado que os cards leem (bug "Recolher todos não faz
// nada", report do usuário 2026-06-10).
const updateCollapsedItems = entries => {
  collapsedItems.value = { ...collapsedItems.value, ...entries };
  localStorage.setItem(
    collapsedStorageKey.value,
    JSON.stringify(collapsedItems.value)
  );
};

loadCardVisibility();
loadCollapsedItems();

// Disponibilizar dados para componentes filhos
provide('labelsMap', labelsMap);
provide('cardVisibility', cardVisibility);
provide('collapsedItems', collapsedItems);
provide('updateCardVisibility', updateCardVisibility);
provide('updateCollapsedItem', updateCollapsedItem);
provide('updateCollapsedItems', updateCollapsedItems);

// O board (kanban) carrega via paginação server-side por coluna. List e agenda
// começam com uma página global limitada e deixam o usuário avançar pelo cursor;
// a troca de visualização nunca bloqueia baixando o pipeline inteiro.
//
// Bug 2026-09-17 (relato Nagano): o `else` cobria QUALQUER view diferente de
// 'pipeline' — inclusive 'pipelines' (o gerenciador de funis) e 'templates',
// que não consomem cards. Numa conta com ZERO pipelines, `selectedPipeline` é
// nulo e `fetchPipelineCardItems` lança "Nenhum pipeline selecionado"; o
// switchView (abaixo) capturava a exceção e revertia `currentView` para a
// view anterior, então "Configurações > Gerenciar Pipelines" no header do
// board nunca chegava à tela que tem o botão "Novo Pipeline" — clicar
// parecia não fazer nada. Só 'list'/'agenda' precisam do fetch global.
const fetchCardsForCurrentView = async (view = currentView.value) => {
  if (view === 'pipeline') {
    await store.dispatch('pipelineCards/fetchBoardByStages');
  } else if (view === 'list' || view === 'agenda') {
    await store.dispatch('pipelineCards/fetchPipelineCardItems');
  }
};

const loadCanonicalView = async (
  view,
  { clearItem = view !== 'item-detail' } = {},
  requestIsCurrent = () => true
) => {
  try {
    await fetchCardsForCurrentView(view);
  } catch {
    // The canonical target still owns the route so its error/retry state must
    // render. Crucially, the mode changes only after the request settles.
  }
  if (!requestIsCurrent()) return false;
  if (clearItem) selectedItemId.value = null;
  currentView.value = view;
  return true;
};

// Track previous pipeline ID to detect changes
const previousPipelineId = ref(null);

// Watch the computed selectedPipeline ID directly for more reliable reactivity
// Using the computed property ensures Vue's reactivity system tracks changes properly
watch(
  () => selectedPipeline.value?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      const accountContext = captureAccountContext(activeAccountId.value);
      // Bootstrap resolves route + target mode deterministically below. Letting
      // this watcher fetch the default board first creates a board/global race
      // for direct list/agenda and invalid routes.
      if (isBootstrapping.value) return;
      // Fix P0 — pipeline_index/with_funnel/board são route records DISTINTOS
      // com o mesmo componente → remount a cada navegação entre eles. No
      // disparo `immediate` do setup (oldId === undefined) o store global
      // ainda aponta para o pipeline da visita ANTERIOR; sem este guard, o
      // board disparava fetches inteiros para o pipeline stale antes de o
      // onMounted resolver o slug da URL (desperdício + corrida que prendia
      // colunas em "Carregando itens..."). Se a rota tem slug e ele difere,
      // retorna sem fetch: o onMounted seleciona o pipeline da rota e este
      // watcher dispara de novo com o id certo. Trocas iniciadas pelo usuário
      // (oldId definido) seguem direto — a URL é atualizada DEPOIS do fetch.
      if (oldId === undefined) {
        const hasRoutePipelineSlug = Boolean(route.params.pipelineSlug);
        const routePipelineId = extractIdFromSlug(route.params.pipelineSlug);
        if (
          hasRoutePipelineSlug &&
          (!routePipelineId || String(routePipelineId) !== String(newId))
        ) {
          return;
        }
      }

      // Update tracking ref
      previousPipelineId.value = newId;

      // Fetch cards for new pipeline (board → por stage; demais → global)
      try {
        await fetchCardsForCurrentView();
        if (!accountContextIsCurrent(accountContext)) return;
        isInitialLoad.value = false;
      } catch {
        // The active view exposes the store error and retry action. Keeping the
        // watcher rejection handled prevents a failed request from becoming an
        // orphaned promise while the view remains usable.
        return;
      }

      // Update URL when pipeline changes (but not on initial load from URL)
      if (oldId && !isBootstrapping.value) {
        const pipeline = store.getters['pipelines/getSelectedPipeline'];
        const routePipelineId = extractIdFromSlug(route.params.pipelineSlug);
        if (!routePipelineId || String(routePipelineId) !== String(newId)) {
          navigateToUrl(generateSlug(pipeline), currentView.value);
        }
      }
    }
  },
  { immediate: true }
);

// Watch for route changes (browser back/forward)
watch(
  () => route.params,
  async newParams => {
    if (
      suppressRouteWatcher ||
      isBootstrapping.value ||
      targetAccountId.value !== activeAccountId.value
    ) {
      return;
    }
    routeRequestId += 1;
    const requestId = routeRequestId;
    const accountContext = captureAccountContext(activeAccountId.value);
    const requestIsCurrent = () =>
      requestId === routeRequestId &&
      !suppressRouteWatcher &&
      accountContextIsCurrent(accountContext);
    const { pipelineSlug, view, itemId } = newParams;
    const hasItemIdParam = itemId !== undefined && itemId !== null;
    const parsedItemId = parseRouteItemId(itemId);

    // Handle pipeline slug change
    if (pipelineSlug) {
      if (isBootstrapping.value) return;

      const pipelineId = extractIdFromSlug(pipelineSlug);
      const pipelines = store.getters['pipelines/getPipelines'];
      const pipeline = pipelines.find(
        candidate => String(candidate.id) === String(pipelineId)
      );

      if (!pipeline || !isPipelineAccessible(pipeline)) {
        const requestedView = parsedItemId
          ? 'item-detail'
          : pathToView[view] || view || 'pipeline';
        await replaceInvalidPipelineRoute(
          pipelines,
          requestedView,
          requestIsCurrent
        );
        return;
      }

      if (pipeline && pipeline.id !== selectedPipeline.value?.id) {
        await store.dispatch('pipelines/setSelectedPipeline', pipeline);
        if (!requestIsCurrent()) return;
      }
    }

    // Route params are untrusted and may be arrays or partially numeric values.
    // Fail closed to the canonical board route instead of opening an ambiguous
    // card ID (parseInt('12abc') used to open card 12).
    if (hasItemIdParam && parsedItemId === null) {
      const pipeline = selectedPipeline.value;
      if (pipeline) {
        await loadCanonicalView('pipeline', {}, requestIsCurrent);
        if (!requestIsCurrent()) return;
        await navigateToUrl(generateSlug(pipeline), 'pipeline', null, true);
      }
      return;
    }

    // Handle view change - redirect to unified dashboard if 'dashboard'
    if (view) {
      if (view === 'dashboard') {
        const pipelineId = pipelineSlug
          ? extractIdFromSlug(pipelineSlug)
          : 'all';
        router.push({
          name: 'pipeline_reports',
          query: { pipeline: pipelineId },
        });
        return;
      }

      const mappedView = pathToView[view] || view;
      if (currentView.value !== mappedView) {
        try {
          // eslint-disable-next-line no-use-before-define
          await switchView(mappedView, false);
          if (!requestIsCurrent()) return;
        } catch {
          // The target view renders the store error and its retry action.
        }
      }
    } else if (pipelineSlug && !itemId && currentView.value !== 'pipeline') {
      try {
        // eslint-disable-next-line no-use-before-define
        await switchView('pipeline', false);
        if (!requestIsCurrent()) return;
      } catch {
        // The board renders the store error and its retry action.
      }
    }

    // Handle item ID change
    if (parsedItemId !== null) {
      if (selectedItemId.value !== parsedItemId) {
        selectedItemId.value = parsedItemId;
        currentView.value = 'item-detail';
        forceReloadCounter.value += 1;
      }
    } else if (currentView.value === 'item-detail' && !itemId) {
      // If we're in item-detail but no itemId, go back to pipeline view
      currentView.value = 'pipeline';
      selectedItemId.value = null;
    }
  },
  { deep: true }
);

// Provide URL helper to child components
provide('generatePipelineSlug', generateSlug);
provide('navigateToUrl', navigateToUrl);

// O cable do board é exclusivo desta tela — o dashboard analítico tem o
// próprio (pipelineAnalyticsCable). Enquanto os dois compartilhavam um
// singleton era preciso contar referências e coordenar posse; sem dono
// compartilhado, o ciclo volta a ser o simples init/disconnect.
const hardResetCable = () => {
  pipelineCardCable.unsubscribe();
  pipelineCardCable.disconnect?.();
};

// Real-time WebSocket: Subscribe to pipeline changes when pipeline changes
watch(
  () => selectedPipeline.value?.id,
  newPipelineId => {
    if (
      isBootstrapping.value ||
      activeAccountId.value !== targetAccountId.value
    ) {
      return;
    }
    // Only change pipeline if cable is initialized (will happen in onMounted)
    if (newPipelineId && pipelineCardCable.isInitialized()) {
      // Change subscription when pipeline changes (uses changePipeline internally)
      pipelineCardCable.changePipeline(newPipelineId);
    }
  }
);

// Modificar onMounted para usar o store e sincronizar com URL
const initializePipelineView = async requestedContext => {
  const context = requestedContext?.accountId
    ? requestedContext
    : captureAccountContext(activeAccountId.value);
  const accountId = context.accountId;
  const requestIsCurrent = () => accountContextIsCurrent(context);
  isBootstrapping.value = true;
  bootstrapError.value = '';

  try {
    loadCardVisibility();
    loadCollapsedItems();
    // 1. Buscar lista de pipelines primeiro
    await store.dispatch('pipelines/fetch', { accountId });
    if (!requestIsCurrent()) return;

    // 1.1 Buscar labels para exibir cores corretas nos cards.
    // Fix P0 — falha aqui NÃO pode abortar o onMounted: sem o restante do
    // fluxo, ninguém seleciona o pipeline da rota nem dispara o fetch do board
    // (tela "Nenhum item" sem nenhuma request).
    let allLabels = [];
    try {
      const response = await LabelsAPI.getForAccount(accountId);
      if (!requestIsCurrent()) return;
      allLabels = Array.isArray(response.data?.payload)
        ? response.data.payload
        : [];
    } catch {
      // Cards renderizam sem cores de label; o board segue funcional.
    }
    if (!requestIsCurrent()) return;
    // Criar mapa de label title -> color para uso nos cards
    labelsMap.value = {};
    allLabels.forEach(label => {
      labelsMap.value[label.title] = label.color;
    });

    const pipelines = store.getters['pipelines/getPipelines'];
    const { pipelineSlug, view, itemId } = route.params;
    const hasItemIdParam = itemId !== undefined && itemId !== null;
    let resolvedItemId = parseRouteItemId(itemId);
    let itemRouteNeedsCanonicalization =
      hasItemIdParam && resolvedItemId === null;
    let canonicalViewLoadAttempted = false;
    let selectedPipelineNeedsUrl = false;

    // Resolve the requested view before handling an invalid slug so the
    // canonical redirect can preserve list/agenda while dropping unsafe item
    // detail IDs that belonged to a missing pipeline.
    let resolvedView = view ? pathToView[view] || view : currentView.value;
    if (itemRouteNeedsCanonicalization) resolvedView = 'pipeline';

    // 2. Se tiver slug na URL, selecionar o pipeline correspondente
    if (pipelineSlug) {
      const pipelineId = extractIdFromSlug(pipelineSlug);
      const pipeline = pipelines.find(
        candidate => String(candidate.id) === String(pipelineId)
      );

      if (pipeline && isPipelineAccessible(pipeline)) {
        await store.dispatch('pipelines/setSelectedPipeline', pipeline);
        if (!requestIsCurrent()) return;
      } else {
        const replacement = await replaceInvalidPipelineRoute(
          pipelines,
          resolvedItemId ? 'item-detail' : resolvedView,
          requestIsCurrent
        );
        if (!replacement) return;
        resolvedItemId = null;
        itemRouteNeedsCanonicalization = false;
        resolvedView = replacement.view;
        canonicalViewLoadAttempted = true;
      }
    }

    // 4. Se tiver itemId na URL, selecionar o item
    if (resolvedItemId !== null) {
      selectedItemId.value = resolvedItemId;
      resolvedView = 'item-detail';
      forceReloadCounter.value += 1;
    }

    // 5. Se não houver pipeline selecionado e não houver slug na URL,
    // selecionar o primeiro pipeline acessível e atualizar URL
    if (!selectedPipeline.value && pipelines.length > 0) {
      const firstAccessible = firstAccessiblePipeline(pipelines);
      if (!firstAccessible) {
        bootstrapError.value = t('PIPELINE.PIPELINE_NOT_FOUND');
        currentView.value = 'pipelines';
        return;
      }
      await store.dispatch('pipelines/setSelectedPipeline', firstAccessible);
      if (!requestIsCurrent()) return;
      selectedPipelineNeedsUrl = true;
    } else if (selectedPipeline.value && !pipelineSlug) {
      // Se já tem pipeline selecionado mas não tem na URL, atualizar URL
      selectedPipelineNeedsUrl = true;
    }

    // 6. Carregar os cards do pipeline selecionado.
    // Fix P0 — garantia determinística pós-remount: quando o pipeline da rota
    // JÁ era o selecionado no setup, o setSelectedPipeline do passo 2 é no-op e
    // o watcher (que só dispara em MUDANÇA de id) não busca de novo — este
    // dispatch é quem garante o load. Duplicidade é inócua: o guard de
    // dedupe/cache do fetchBoardByStages absorve o segundo dispatch.
    if (selectedPipeline.value?.id) {
      if (!canonicalViewLoadAttempted) {
        await loadCanonicalView(resolvedView, {}, requestIsCurrent);
      }
      if (!requestIsCurrent()) return;
      isInitialLoad.value = false;

      if (itemRouteNeedsCanonicalization) {
        await navigateToUrl(
          generateSlug(selectedPipeline.value),
          'pipeline',
          null,
          true
        );
      } else if (selectedPipelineNeedsUrl) {
        await navigateToUrl(generateSlug(selectedPipeline.value), resolvedView);
      }
      if (!requestIsCurrent()) return;
    }

    // 7. Initialize real-time WebSocket connection for pipeline updates
    const currentUser = store.getters.getCurrentUser;
    if (currentUser?.pubsub_token && requestIsCurrent()) {
      if (
        pipelineCardCable.isInitialized() &&
        normalizeAccountId(pipelineCardCable.currentAccountId) !== accountId
      ) {
        hardResetCable();
      }
      pipelineCardCable.init(store, currentUser.pubsub_token);

      // Subscribe to selected pipeline if already available
      if (selectedPipeline.value?.id) {
        pipelineCardCable.subscribe(selectedPipeline.value.id);
      }
    }
  } catch {
    if (requestIsCurrent()) {
      bootstrapError.value = t('PIPELINE.ERRORS.PIPELINE_FETCH_FAILED');
    }
  } finally {
    if (requestIsCurrent()) {
      isBootstrapping.value = false;
      suppressRouteWatcher = false;
    }
  }
};

const resetTenantViewState = () => {
  routeRequestId += 1;
  viewTransitionRequestId += 1;
  forceReloadCounter.value += 1;
  selectedItemId.value = null;
  previousPipelineId.value = null;
  currentView.value = 'pipeline';
  isInitialLoad.value = true;
  isTransitioning.value = false;
  bootstrapError.value = '';
  labelsMap.value = {};
  cardVisibility.value = {};
  collapsedItems.value = {};
};

const beginAccountTransition = accountId => {
  const normalizedAccount = normalizeAccountId(accountId);
  if (normalizedAccount === null) return;

  accountGeneration += 1;
  bootstrapStartedGeneration = -1;
  activeAccountId.value = normalizedAccount;
  suppressRouteWatcher = true;
  isBootstrapping.value = true;
  hardResetCable();
  resetTenantViewState();
  store.dispatch('pipelines/reset');
  store.dispatch('pipelineCards/resetForAccountSwitch');
};

const startAccountBootstrapIfReady = () => {
  const accountId = targetAccountId.value;
  if (
    !isViewMounted ||
    !accountContextIsReady(accountId) ||
    bootstrapStartedGeneration === accountGeneration
  ) {
    return;
  }

  bootstrapStartedGeneration = accountGeneration;
  initializePipelineView(captureAccountContext(accountId));
};

watch(
  [routeAccountId, normalizedCurrentAccountId],
  () => {
    const accountId = targetAccountId.value;
    if (accountId === null) return;
    const accountIdentityMismatch =
      routeAccountId.value !== null &&
      normalizedCurrentAccountId.value !== null &&
      routeAccountId.value !== normalizedCurrentAccountId.value;
    if (
      accountId !== activeAccountId.value ||
      (accountIdentityMismatch && !suppressRouteWatcher)
    ) {
      beginAccountTransition(accountId);
    }
    startAccountBootstrapIfReady();
  },
  { flush: 'sync' }
);

onMounted(() => {
  isViewMounted = true;
  const accountId = targetAccountId.value;
  activeAccountId.value = accountId;
  suppressRouteWatcher = true;
  startAccountBootstrapIfReady();
});

// Cleanup WebSocket connection on unmount.
// `release()` em vez de `disconnect()`: o dashboard analítico usa o MESMO
// singleton e, na troca de rota, as duas telas ficam montadas por um instante.
// Um disconnect aqui derrubava o consumer que o dashboard acabou de criar.
// (fase-57 RT-P1-7)
onUnmounted(() => {
  isViewMounted = false;
  accountGeneration += 1;
  viewTransitionRequestId += 1;
  pipelineCardCable.unsubscribe();
  pipelineCardCable.disconnect?.();
});

async function switchView(view, updateUrl = true) {
  // Redirect to unified dashboard when 'dashboard' is selected
  if (view === 'dashboard') {
    const pipelineId = selectedPipeline.value?.id || 'all';
    await router.push({
      name: 'pipeline_reports',
      query: { pipeline: pipelineId },
    });
    return true;
  }

  if (view === currentView.value) return true;

  viewTransitionRequestId += 1;
  const transitionRequestId = viewTransitionRequestId;
  const previousView = currentView.value;
  const previousItemId = selectedItemId.value;

  // Keep feedback tied to the real data request; an artificial minimum delay
  // made every view switch feel slower, even when the data was cached.
  isTransitioning.value = true;
  try {
    // O cache é mode-aware ('board' vs 'global'). A troca só termina quando a
    // primeira página do modo novo está disponível.
    await fetchCardsForCurrentView(view);

    if (transitionRequestId !== viewTransitionRequestId) return false;

    currentView.value = view;
    selectedItemId.value = null;
    if (updateUrl) {
      if (selectedPipeline.value) {
        await navigateToUrl(generateSlug(selectedPipeline.value), view);
      }
    }
    return true;
  } catch {
    if (transitionRequestId === viewTransitionRequestId) {
      if (updateUrl) {
        currentView.value = previousView;
        selectedItemId.value = previousItemId;
      } else {
        // Browser already owns this route. Show the target mode's error/retry
        // state, but only after its load attempt has settled.
        currentView.value = view;
        selectedItemId.value = null;
      }
    }
    return false;
  } finally {
    await nextTick();
    if (transitionRequestId === viewTransitionRequestId) {
      isTransitioning.value = false;
    }
  }
}

// Função para visualizar detalhes do item
const handleItemClick = item => {
  // Guarda de payload: o PipelineColumn já escutou `@click` no PipelineCard,
  // que não declara esse emit — o listener virava nativo e repassava um
  // MouseEvent no lugar do card. O resultado era navegar para `/item` sem id,
  // montar o detalhe com itemId nulo e o card simplesmente não abrir, sem
  // nenhum erro visível (report 2026-08-05). Falhar alto é melhor que navegar
  // para um estado quebrado.
  const itemId = item?.id;
  if (itemId === undefined || itemId === null) {
    // eslint-disable-next-line no-console
    console.error('[Pipeline] item-click recebeu payload sem id', item);
    return;
  }

  selectedItemId.value = itemId;
  forceReloadCounter.value += 1;
  currentView.value = 'item-detail';

  // Update URL with item detail
  if (selectedPipeline.value) {
    navigateToUrl(generateSlug(selectedPipeline.value), 'item-detail', itemId);
  }
};

// Voltar para a view anterior
const handleBackFromDetail = () => {
  currentView.value = 'pipeline'; // ou guardar a última view ativa
  selectedItemId.value = null;

  // Update URL to remove item detail
  if (selectedPipeline.value) {
    navigateToUrl(generateSlug(selectedPipeline.value), 'pipeline');
  }
};

// Simplificar para usar o store para atualizações
const handleItemsUpdated = async () => {
  await fetchCardsForCurrentView();
};

const getCurrentComponent = computed(() => {
  switch (currentView.value) {
    case 'pipeline':
      return PipelineTab;
    case 'list':
      return ListTab;
    case 'agenda':
      return AgendaTab;
    case 'pipelines':
      return PipelinesManager;
    case 'templates':
      return MessageTemplates;
    case 'item-detail':
      return PipelineCardDetailView;
    default:
      return PipelineTab;
  }
});
</script>

<template>
  <div class="relative flex h-full min-h-0 w-full min-w-0 overflow-hidden">
    <!-- Loading Overlay (Phase 1 - UX Improvements) -->
    <Transition
      enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
      leave-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isTransitioning"
        class="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <Spinner size="small" color-scheme="primary" />
        <span class="sr-only">{{ t('PIPELINE.LOADING') }}</span>
      </div>
    </Transition>

    <div
      v-if="isBootstrapping"
      class="flex h-full w-full items-center justify-center gap-3 text-sm text-slate-600 dark:text-slate-300"
      role="status"
      aria-live="polite"
    >
      <Spinner size="small" color-scheme="primary" />
      <span>{{ t('PIPELINE.LOADING') }}</span>
    </div>

    <div
      v-else-if="bootstrapError"
      class="m-auto flex max-w-md flex-col items-center gap-4 px-6 text-center"
      role="alert"
    >
      <span
        class="i-lucide-triangle-alert size-8 text-red-500"
        aria-hidden="true"
      />
      <p class="text-sm text-slate-700 dark:text-slate-200">
        {{ bootstrapError }}
      </p>
      <button
        type="button"
        class="min-h-11 rounded-lg bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-woot-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
        @click="initializePipelineView"
      >
        {{ t('PIPELINE.ERRORS.RETRY') }}
      </button>
    </div>

    <keep-alive v-else>
      <component
        :is="getCurrentComponent"
        :current-view="currentView"
        :pipeline-cards="pipelineCards"
        :labels-map="labelsMap"
        :is-loading="isLoading"
        :item-id="selectedItemId"
        :force-reload="forceReloadCounter"
        @switch-view="switchView"
        @items-updated="handleItemsUpdated"
        @item-click="handleItemClick"
        @back="handleBackFromDetail"
        @item-updated="handleItemsUpdated"
      />
    </keep-alive>
  </div>
</template>
